import { PrintSettingsStateTypes } from "../types/print_settings";
import { Product } from "../types/product";

export type PrintLabelKind = "display" | "barcode";

export interface PrintLabelSpec {
  product: Product;
  kind: PrintLabelKind;
}

const quantityFor = (product: Product, settings: PrintSettingsStateTypes) =>
  Math.max(0, Number(product.printQty ?? settings.printQuantity ?? 0));

export const getPrintGroupingMode = (
  mode: PrintSettingsStateTypes["printGrouping"],
) => (mode === "row" ? "row" : "item");

export const buildPrintGroups = (
  products: Product[],
  settings: PrintSettingsStateTypes,
  isPreview = false,
): PrintLabelSpec[][] => {
  const perLine = Math.max(1, Number(settings.printPerLine) || 1);
  const groupsPerItem = products.map((product) => {
    const displayQuantity = settings.showDisplay
      ? Math.max(0, Number(settings.displayQuantity) || 0)
      : 0;
    const labels: PrintLabelSpec[] = [];

    for (let index = 0; index < displayQuantity; index += 1) {
      labels.push({ product, kind: "display" });
    }
    const barcodeQuantity = isPreview
      ? Math.min(quantityFor(product, settings), perLine)
      : quantityFor(product, settings);
    for (let index = 0; index < barcodeQuantity; index += 1) {
      labels.push({ product, kind: "barcode" });
    }

    return labels;
  });

  if (getPrintGroupingMode(settings.printGrouping) === "item") {
    return groupsPerItem.flatMap((labels) => {
      const groups: PrintLabelSpec[][] = [];
      for (let index = 0; index < labels.length; index += perLine) {
        groups.push(labels.slice(index, index + perLine));
      }
      return groups;
    });
  }

  const flattened = groupsPerItem.flat();
  const groups: PrintLabelSpec[][] = [];
  for (let index = 0; index < flattened.length; index += perLine) {
    groups.push(flattened.slice(index, index + perLine));
  }
  return groups;
};
